/**
 * Logout mutation — calls POST /api/auth/logout, then clears the
 * entire query cache and forces a page reload.
 *
 * The reload is the simplest path to a clean post-logout state:
 *   - All mutations cancelled implicitly.
 *   - All in-flight queries discarded.
 *   - The Shell remounts with `useMe` returning UnauthenticatedError
 *     because the session cookie is gone.
 *
 * We could instead invalidate ["me"] and let the Shell render the
 * SignIn view, but a reload guarantees no stray state survives
 * (timers, refs, websocket reconnect attempts). The Phase-2 surface is
 * small enough that the reload cost is negligible.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPost } from "./api";

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, Error, void>({
    mutationFn: async () => apiPost<{ ok: true }>("/api/auth/logout", {}),
    onSuccess: () => {
      queryClient.clear();
      if (typeof window !== "undefined") {
        window.location.reload();
      }
    },
  });
}
